// ────────────────────────────────────────────────────────────────────────────
// USE CASES AREA — content model (CP4)
// Single source of truth for the /use-cases hub + 3 department pages + 3 workflow
// pages, so the hub, the two page templates, the nav mega-menu and the SEO entries
// all read from one place (no drift between them).
//
// "Two ways in" (D7): BY DEPARTMENT (who it's for — messaging) and BY WORKFLOW
// (what it does — a stitched deep-dive + product entry). Distinct jobs so they
// complement, never duplicate — they cross-link (D5).
//
// Grounding (D14): department taglines + possibility bullets come from mock.js
// `useCases`; workflow steps from mock.js `collaborationSteps` + the RevenueRecovery /
// AiAutomationWorkflows landing copy. Pipeline Generation is authored fresh and held to
// the honesty guardrail (D15 / D8a): the agents run the GTM steps and draft — your team
// approves before anything goes out. Nothing here implies a shipping autonomous engine.
// ────────────────────────────────────────────────────────────────────────────

import { agentsDirectory } from './agentsDirectory';

// Resolve agent ids → full directory entries (name, role, slug, thumb, brand color),
// dropping any unknown id so a typo never renders a blank chip.
export const resolveAgents = (ids = []) =>
  ids.map((id) => agentsDirectory.find((a) => a.id === id)).filter(Boolean);

// ── Departments — "Implify for your team" (who-it's-for messaging) ──────────
export const departments = [
  {
    slug: 'sales',
    navLabel: 'Sales',
    menuDesc: 'Close more, less manual work',
    // Grounded: mock.js useCases → Sales.
    title: 'Close more deals with less manual work',
    lead:
      'Your sales team spends more time updating the CRM and chasing follow-ups than actually selling. Implify hands that work to a team of AI agents — so your reps walk into every call prepped and spend their hours on the deals, not the admin.',
    // Grounded: mock.js useCases.sales.outcomes (+ recovery from RevenueRecovery).
    possibilities: [
      { title: 'Leads qualified and prioritized', body: 'Every inbound lead scored and sorted, so reps work the ones most likely to close first.' },
      { title: 'Follow-ups sent on time, every time', body: 'No deal goes cold because someone forgot to reply — the follow-up is drafted and queued at the right moment.' },
      { title: 'A CRM that tells the truth', body: 'Stages, notes and next steps kept current automatically, so the pipeline you look at is the pipeline you actually have.' },
      { title: 'Deal insight before every call', body: 'Walk in already briefed — who you’re meeting, where the deal stands, and the smartest next move.' },
      { title: 'Quiet pipeline recovered', body: 'Stalled and forgotten deals resurfaced with a reason to reach back out, so revenue you already earned doesn’t leak away.' },
      { title: 'Reps focused on selling', body: 'The busywork moves to the agents; your team’s time goes back to conversations and closing.' },
    ],
    // Before/After value section — the multi-agent story woven in (each "after" names the
    // agent that handles it). `after` reads as "<AgentName> <after>". D8a: agents draft/prep.
    beforeAfter: {
      heading: 'The difference in a sales team’s day',
      sub: 'Same team, same tools — but the manual work moves to your AI teammates. Here’s what changes.',
      rows: [
        { agent: 'dora', before: 'Reps rebuild context from scattered tabs before every call.', after: 'briefs every call — who you’re meeting, where the deal stands, and the smartest next move.' },
        { agent: 'sally', before: 'The CRM drifts out of date, so no one trusts the forecast.', after: 'keeps stages and fields current, so the pipeline tells the truth.' },
        { agent: 'sally', before: 'Follow-ups slip the moment things get busy.', after: 'drafts and queues every due follow-up — you just approve and send.' },
        { agent: 'stark', before: 'Stalled deals go quiet and quietly leak revenue.', after: 'resurfaces stalled deals with a reason to reach back out.' },
      ],
    },
    // "See it in action" (Section B) — a big anchor (the command center) + alternating
    // product-UI rows, Sally woven into each. `visual` is the placeholder label (D19);
    // real captures swap in at the image pass → public/images/use-cases/sales/*.png.
    showcase: {
      intro: {
        heading: 'One place to run your whole sales motion',
        body: 'Deals, pipeline, outreach and reporting in a single workspace — with Sally working it in the background. She flags the deals at risk, drafts the next move, and brings the decisions that need a human straight to you. You approve what matters; the routine runs itself.',
      },
      anchorVisual: 'Sales command center · alerts · decisions · live agent activity',
      rows: [
        {
          agent: 'sally',
          heading: 'Every deal comes with the next move',
          body: 'Sally reads each deal — stage, blockers, buying signals — and posts the insight and recommended next step right on the card. Reps open a deal already knowing what to do, instead of digging through history.',
          visual: 'Deals board · Sally’s insight + next move on every card',
        },
        {
          agent: 'sally',
          heading: 'A forecast you can actually trust',
          body: 'Pipeline by stage, forecast vs. target, win rate and loss reasons — kept current automatically. You see exactly where the quarter stands and what’s dragging it, without building a spreadsheet.',
          visual: 'Pipeline · stages · forecast vs. target · loss reasons',
        },
        {
          agent: 'sally',
          heading: 'Outreach that keeps booking meetings',
          body: 'Approved sequences run, track their own replies and report their meeting rates — so follow-up never stalls. Sally flags which sequences to lean into and which to fix.',
          visual: 'Outreach · active sequences · reply rates · meetings booked',
        },
        {
          agent: 'sally',
          heading: 'See what’s actually working',
          body: 'Win/loss reasons, rep performance and revenue trends, assembled for you — with Sally surfacing the patterns worth acting on, like win rate dropping when the first demo slips.',
          visual: 'Reports · win/loss · rep performance · revenue trend',
        },
      ],
    },
    agents: ['sally', 'stark', 'dora'],
    // Cross-link to the stitched workflows this team runs (D5 — referenced, not duplicated).
    workflows: ['crm-management', 'pipeline-generation', 'focus-and-flow'],
    seo: {
      title: 'Implify for Sales — close more, less manual work | Implify',
      description:
        'Implify for Sales: AI agents qualify leads, keep your CRM true, brief every call and recover quiet pipeline — so your reps spend their time selling, not on admin.',
      keywords: 'AI for sales, sales automation, CRM AI, lead qualification, sales follow-up automation, AI sales assistant',
    },
  },
  {
    slug: 'marketing',
    navLabel: 'Marketing',
    menuDesc: 'Ship campaigns without the chaos',
    // Grounded: mock.js useCases → Marketing.
    title: 'Ship campaigns without the coordination chaos',
    lead:
      'Marketing runs on a dozen moving parts across as many tools. Implify gives you a team of AI agents that keep the calendar moving, the channels published and the reporting current — so launches ship on time without the endless coordination.',
    // Grounded: mock.js useCases.marketing.outcomes.
    possibilities: [
      { title: 'A content calendar that runs itself', body: 'Briefs, drafts and scheduling kept moving, so the calendar stays full instead of stalling on one bottleneck.' },
      { title: 'Campaign performance, always current', body: 'Results pulled together and reported without a manual spreadsheet pass every Monday morning.' },
      { title: 'Social scheduled and watched', body: 'Posts drafted, queued across platforms and monitored — with the replies that matter surfaced to you.' },
      { title: 'Lead nurturing on autopilot', body: 'Sequences that keep prospects warm across channels, drafted for your approval before they send.' },
      { title: 'On-brand by default', body: 'Every draft works from the same understanding of your voice, product and audience — not a generic template.' },
      { title: 'Launches that actually launch', body: 'The moving parts stay coordinated across your stack, so the campaign ships when you said it would.' },
    ],
    beforeAfter: {
      heading: 'The difference in a marketing team’s week',
      sub: 'Same team, same tools — but the coordination and busywork move to your AI teammates. Here’s what changes.',
      rows: [
        { agent: 'marsi', before: 'The content calendar stalls the moment one person is underwater.', after: 'keeps the calendar moving — briefs assigned, drafts progressing — so nothing waits on one bottleneck.' },
        { agent: 'wright', before: 'Every asset ends up sounding a little different.', after: 'drafts on-brand copy from one shared voice, so everything sounds like you.' },
        { agent: 'buzz', before: 'Social gets posted late, and replies go unseen.', after: 'schedules across platforms and surfaces the replies that actually matter.' },
        { agent: 'marsi', before: 'Reporting is a manual spreadsheet slog every Monday.', after: 'pulls campaign performance together automatically, ready when you open it.' },
      ],
    },
    // "See it in action" (Section B) — matched to the real Marketing captures. `visual`
    // is the placeholder label (D19); real shots swap in at the image pass.
    showcase: {
      intro: {
        heading: 'One place to run every campaign',
        body: 'Campaigns, content, social and analytics in a single workspace — with Marsi, Wright and Buzz working in the background. They monitor what’s live, draft what’s next, and flag the calls that need you, like moving budget off an underperforming channel. You approve; the rest runs itself.',
      },
      anchorVisual: 'Marketing command center · campaigns · alerts · decisions · channel performance',
      rows: [
        {
          heading: 'Every campaign, watched and tuned',
          body: 'Every campaign in one view — budget, impressions, CTR, leads and cost per lead, live. Marsi watches performance and flags what to change, like narrowing an audience that’s underdelivering, before the spend is wasted.',
          visual: 'Campaigns · budgets · CTR · leads · CPL · Marsi’s recommendations',
        },
        {
          heading: 'A content pipeline that keeps moving',
          body: 'Briefs, drafts, SEO and scheduling on one board — Wright drafts each piece, scores it for SEO, and moves it to review for your approval. You see exactly what’s in flight and what’s ranking, without chasing anyone.',
          visual: 'Content · draft → review → scheduled → published · SEO scores',
        },
        {
          heading: 'Social scheduled across every channel',
          body: 'A full week of posts queued across LinkedIn, X, Instagram, Facebook and TikTok — Buzz drafts and schedules them, tracks what lands, and surfaces the replies worth your attention.',
          visual: 'Social · weekly calendar · scheduled posts · per-channel engagement',
        },
        {
          heading: 'The whole funnel, in numbers you trust',
          body: 'Visitors to MQLs to SQLs to pipeline — the full funnel and every channel’s ROI, assembled for you. The patterns that matter get surfaced, like a channel burning budget at a low return, so you can act before the quarter is decided.',
          visual: 'Analytics · funnel · channel ROI · key trends',
        },
      ],
    },
    agents: ['marsi', 'buzz', 'wright'],
    workflows: ['pipeline-generation', 'focus-and-flow', 'crm-management'],
    seo: {
      title: 'Implify for Marketing — ship campaigns without the chaos | Implify',
      description:
        'Implify for Marketing: AI agents keep your content calendar moving, schedule and monitor social, track campaign performance and nurture leads — on-brand, on time.',
      keywords: 'AI for marketing, marketing automation, content calendar AI, campaign reporting, social media automation, lead nurturing AI',
    },
  },
  {
    slug: 'personal-productivity',
    navLabel: 'Personal Productivity',
    menuDesc: 'Work like you have a full team',
    // Grounded: mock.js useCases → Solo Professionals (persona cut, not a literal org function).
    title: 'Work like you have a full team',
    lead:
      'When it’s just you — or a small team wearing every hat — the admin never ends. Implify gives you a set of AI agents that plan your day, handle the follow-through and keep projects on track, so you get the leverage of a full team without hiring one.',
    // Grounded: mock.js useCases.solo.outcomes.
    possibilities: [
      { title: 'Your day, planned overnight', body: 'Wake up to a prioritized schedule with briefs and action items already assembled — not a blank to-do list.' },
      { title: 'Client comms handled professionally', body: 'Replies, updates and check-ins drafted in your voice, ready for a quick approve-and-send.' },
      { title: 'Projects that stay on track', body: 'Deadlines, deliverables and next steps tracked, with nudges before anything slips.' },
      { title: 'Admin that does itself', body: 'The recurring busywork — scheduling, chasing, tidying — moves off your plate to the agents.' },
      { title: 'Nothing falls through the cracks', body: 'Every thread and commitment is watched, so follow-ups happen at the right time without you remembering.' },
      { title: 'Focus for the real work', body: 'The leverage of a team behind you means your hours go to the work only you can do.' },
    ],
    // Luc-centric (your personal chief-of-staff runs the whole day).
    beforeAfter: {
      heading: 'The difference in your day',
      sub: 'Same you, same tools — but the planning, the inbox and the decisions move to Luc, your AI chief-of-staff. Here’s what changes.',
      rows: [
        { agent: 'luc', before: 'You start the day building a to-do list from scratch.', after: 'has a prioritized plan waiting when you wake up.' },
        { agent: 'luc', before: 'The inbox runs your day.', after: 'triages it and drafts the replies, surfacing only the few that need you.' },
        { agent: 'luc', before: 'Approvals and decisions pile up across a dozen tabs.', after: 'brings the calls that need you into one place, and handles the routine.' },
        { agent: 'luc', before: 'You walk into meetings still getting up to speed.', after: 'has a brief ready before every one.' },
      ],
    },
    // "See it in action" (Section B) — matched to the real Home / "My Day" captures. PP
    // has one surface (Home, run by Luc), captured for three roles; the section breaks it
    // into its jobs and closes on "it adapts to your role". `visual` = placeholder label (D19).
    showcase: {
      intro: {
        heading: 'Your whole day, in one morning brief',
        body: 'Personal Productivity isn’t a separate app — it’s your Home, run by Luc. Every morning Luc pulls your inbox, calendar, tasks and the decisions waiting on you into one brief, drafts what it can, and flags the few things that actually need you. You skim, approve, and get on with the real work.',
      },
      anchorVisual: 'My Day · morning brief · inbox · meetings · must-do · approvals',
      rows: [
        {
          heading: 'Your day, planned overnight',
          body: 'Luc assembles a prioritized plan before you’re up — today’s meetings and the must-do, time-sensitive tasks — so you start knowing what to do first instead of building the list yourself.',
          visual: 'My Day · meetings · today’s tasks · must-do',
        },
        {
          heading: 'An inbox that already has your replies',
          body: 'The inbox that used to run your day comes pre-triaged — the threads that matter surfaced, replies drafted in your voice, ready for a one-click send. The rest stays out of your way.',
          visual: 'Inbox snapshot · drafted replies · reply / more',
        },
        {
          heading: 'Only the calls that need you',
          body: 'Approvals, risks and decisions land in one place — a budget to sign off, a risk flagged, a conflict to resolve. Luc handles the routine and brings you the exceptions. You approve; the rest runs itself.',
          visual: 'Needs your decision · approvals · risks flagged',
        },
        {
          heading: 'It adapts to whatever you run',
          body: 'Founder, marketing lead, or head of people — the same command center flexes to your role, pulling the right specialists’ work into your morning. One brief, whatever your job.',
          visual: 'My Day, adapted · founder · marketing lead · people lead',
        },
      ],
    },
    agents: ['luc', 'zira', 'finn'],
    workflows: ['focus-and-flow', 'crm-management', 'pipeline-generation'],
    seo: {
      title: 'Implify for Personal Productivity — work like you have a full team | Implify',
      description:
        'Implify for solo professionals and small teams: AI agents plan your day, handle client comms and follow-through, and keep projects on track — the leverage of a full team.',
      keywords: 'AI personal productivity, AI assistant for solopreneurs, AI for small teams, daily planning AI, task automation, follow-up automation',
    },
  },
];

// ── Workflows — one stitched outcome each, in depth + product entry ─────────
// `steps` reuse the ScrollPinnedSteps shape ({ title, headline, desc, visual }).
export const workflows = [
  {
    slug: 'crm-management',
    navLabel: 'CRM management',
    menuDesc: 'A clean pipeline, no busywork',
    origin: 'real', // grounded in shipped copy
    title: 'A clean pipeline without the busywork',
    lead:
      'CRM management is really three jobs stitched together — showing up to calls prepped, keeping records honest, and never dropping a follow-up. Implify runs all three so your pipeline stays true and current without anyone babysitting it.',
    steps: [
      {
        title: 'Pre-meeting deal brief',
        headline: 'Walk into every call already prepped',
        desc: 'Before each meeting, an agent pulls together who you’re talking to, where the deal stands, recent activity and the smartest next move — so you open the call informed instead of scrambling through tabs.',
        visual: 'Deal brief · who, stage, next move',
      },
      {
        title: 'CRM hygiene check',
        headline: 'Records that tell the truth',
        desc: 'Stale stages, missing fields and out-of-date notes get flagged and fixed continuously — so the pipeline you report on matches reality, without the Friday-afternoon clean-up.',
        visual: 'Hygiene check · flagged & fixed fields',
      },
      {
        title: 'Daily follow-up list',
        headline: 'Every nudge that’s due, by 9am',
        desc: 'Each morning you get the list of deals that need a touch, each with the exact next step and a draft ready to go. You approve; nothing slips because someone forgot.',
        visual: 'Daily follow-up queue · draft → approve',
      },
    ],
    stitch: {
      heading: 'Three jobs that keep your pipeline honest',
      sub: 'Run any one on its own — a pre-call brief here, a hygiene sweep there. Switch on all three and they hand work between each other, so your CRM stays true from first touch to close.',
      together: {
        heading: 'Run together, your sales motion runs itself',
        body: 'Brief, hygiene and follow-up aren’t three chores — they’re one loop. The pre-call brief is sharp because hygiene keeps the record clean; the follow-up list is right because the brief flagged what’s due. Switch all three on and your sales team stops managing the CRM and starts working the pipeline it hands them — every rep prepped, every record true, every deal followed up.',
        visual: 'Sales cockpit · briefs · clean pipeline · today’s follow-ups',
      },
    },
    // Six benefits shown as a 3×2 grid. #1 carries the D8a/D15 honesty guardrail, #2 the
    // stack fit; the rest are the payoff of running the workflow.
    benefits: [
      { title: 'You stay in control', body: 'The agent drafts every update and follow-up; your team approves before anything reaches a customer or overwrites a record.' },
      { title: 'Fits your stack', body: 'Works with the CRM you already run on — HubSpot, Salesforce, Zoho and more. No migration, no rebuild.' },
      { title: 'Reps walk in prepped', body: 'Every call opens with a brief, not a scramble through tabs.' },
      { title: 'A pipeline you can forecast on', body: 'Clean stages and current fields mean the pipeline you report is the pipeline you actually have.' },
      { title: 'No deal left cold', body: 'Every due follow-up is drafted and queued, so nothing slips for want of a nudge.' },
      { title: 'Set up once, runs daily', body: 'Switch it on and the loop runs every morning in the background — no babysitting.' },
    ],
    intent: 'crm-management',
    departments: ['sales'],
    agents: ['sally', 'dora', 'stark'],
    seo: {
      title: 'CRM management workflow — a clean pipeline, no busywork | Implify',
      description:
        'A stitched CRM-management workflow: pre-meeting deal briefs, continuous CRM hygiene and a daily follow-up list — drafted by AI agents, approved by your team. Works with HubSpot, Salesforce and Zoho.',
      keywords: 'CRM management AI, CRM hygiene automation, deal briefs, sales follow-up workflow, HubSpot AI, Salesforce AI',
    },
  },
  {
    slug: 'pipeline-generation',
    navLabel: 'Pipeline Generation',
    menuDesc: 'Research → message → outreach',
    origin: 'new', // D15 — authored fresh, honesty-reviewed
    title: 'Turn research into pipeline you approve',
    lead:
      'Building pipeline is a chain of steps that usually eats a whole team’s week — researching accounts, finding the angle, and drafting outreach that doesn’t read like a template. Implify’s agents run that chain and bring you drafts to approve; you stay the one who decides what actually goes out.',
    steps: [
      {
        title: 'Market & account research',
        headline: 'The homework, done for you',
        desc: 'Agents research your target accounts and contacts — what they do, recent signals, why now — and assemble it into a short, usable brief per prospect, so you start from insight instead of a blank list.',
        visual: 'Account research brief · signals & angle',
      },
      {
        title: 'Personalized outreach, drafted',
        headline: 'A relevant message per prospect — for your approval',
        desc: 'From that research, agents draft outreach tailored to each account’s context and your voice. Every message lands in a queue for you to edit or approve — the agents prepare the outreach, they don’t fire it off on their own.',
        visual: 'Outreach draft · per-account, in your voice',
      },
      {
        title: 'Campaign & launch',
        headline: 'Approve, then let it run',
        desc: 'Approve a sequence and the agents handle the mechanics — scheduling, tracking replies, and flagging the ones worth your attention. You set what runs on its own and what always waits for a human yes.',
        visual: 'Campaign view · approved sequence running',
      },
    ],
    stitch: {
      heading: 'From cold research to outreach you approve',
      sub: 'Each step earns its keep alone — research you can actually use, drafts you can actually send. Chained together, they turn a blank list into a steady stream of pipeline, with you approving every message.',
      together: {
        heading: 'Run together, it’s a GTM engine your team approves',
        body: 'Research feeds the drafting, the drafting feeds the campaign, and the campaign’s replies feed the next round of research. As one loop it turns sales and marketing from a standing start into a steady flow of qualified pipeline — the agents do the homework and the writing, your team decides what actually goes out. Growth you can scale without scaling headcount.',
        visual: 'GTM engine · research → drafts → campaign, with approvals',
      },
    },
    // Six benefits (3×2). #1 is the D8a/D15 honesty guardrail (critical for this page —
    // no over-claiming autonomy), #2 the stack fit; the rest are the payoff.
    benefits: [
      { title: 'Nothing sends on its own', body: 'The agents do the research and draft the outreach; nothing goes out until you approve it. Not an autopilot that blasts strangers.' },
      { title: 'Fits your stack', body: 'Plugs into the tools you already use for outreach and your CRM — no separate outbound platform to adopt.' },
      { title: 'A repeatable motion', body: 'Not one-off blasts — a loop that keeps producing qualified pipeline week after week.' },
      { title: 'Sales & marketing aligned', body: 'Both teams work the same research-to-outreach loop, off one shared context.' },
      { title: 'Relevant, not spray-and-pray', body: 'Every message is built from real account research, written in your voice.' },
      { title: 'Scales without headcount', body: 'More pipeline without adding to the team that has to build it.' },
    ],
    intent: 'pipeline-generation',
    departments: ['sales', 'marketing'],
    agents: ['dora', 'sally', 'wright'],
    seo: {
      title: 'Pipeline Generation workflow — research to outreach you approve | Implify',
      description:
        'A stitched pipeline-generation workflow: AI agents research accounts and draft personalized outreach — you approve before anything sends. Not an autopilot; a team that does the homework.',
      keywords: 'pipeline generation AI, GTM automation, account research AI, personalized outreach, sales prospecting AI, outbound with approval',
    },
  },
  {
    slug: 'focus-and-flow',
    navLabel: 'Focus & flow',
    menuDesc: 'Your day, run for you',
    origin: 'real',
    title: 'Your day, run for you',
    lead:
      'The work that breaks your focus is rarely the important work — it’s the planning, the inbox, and keeping up with what’s happening around you. Implify’s agents run that layer in the background so you can stay in flow on what actually matters.',
    steps: [
      {
        title: 'Day planning',
        headline: 'Your schedule plans itself',
        desc: 'Overnight, agents assemble a prioritized plan for your day — meetings, deadlines and the action items that matter most — so you start already knowing what to do first instead of building the list yourself.',
        visual: 'Morning plan · prioritized day',
      },
      {
        title: 'Inbox & follow-through',
        headline: 'Nothing falls through the cracks',
        desc: 'Agents watch every thread and commitment, draft the replies and follow-ups they can, and surface the few that need you — so the inbox stops running your day.',
        visual: 'Inbox triage · drafts & follow-ups',
      },
      {
        title: 'Daily digest & competitor insights',
        headline: 'Caught up before you start',
        desc: 'Overnight, agents assemble your daily digest — a short, prioritized read list from the topics and sources you follow — and scan the news for competitor moves, flagging what actually matters. You start the day informed, without trawling feeds and headlines yourself.',
        visual: 'Daily digest · read list + competitor news',
      },
    ],
    stitch: {
      heading: 'Your day, handled in three layers',
      sub: 'Use whichever fits the day — just the planning, just the inbox, just the daily digest. Run all three and they cover for each other in the background, so your whole day organizes itself.',
      together: {
        heading: 'Run together, your whole day organizes itself',
        body: 'Planning sets the day, the inbox layer keeps it from derailing, and the daily digest keeps you current on what’s happening — including your competitors. As one system they cover for each other in the background — the plan updates when a new commitment lands, and a competitor move worth knowing surfaces alongside the day’s read. You get the leverage of a full support team: the routine runs itself, and only what genuinely needs you reaches you.',
        visual: 'Daily command center · plan · inbox · next meeting',
      },
    },
    // Six benefits (3×2). #1 the honesty guardrail (D8a), #2 the stack fit; the rest payoff.
    benefits: [
      { title: 'You stay in control', body: 'Agents draft and prepare; you decide what actually sends. The routine runs itself, the exceptions come to you.' },
      { title: 'Fits your day', body: 'Works across the calendar, inbox and docs you already use — Gmail, Google Calendar, Drive and more.' },
      { title: 'A self-planning day', body: 'Your schedule assembles overnight and re-plans itself as things change.' },
      { title: 'An inbox that behaves', body: 'Threads watched, replies drafted, and only the important few surfaced to you.' },
      { title: 'Informed by default', body: 'A daily digest and competitor watch run in the background, so you start the day caught up — without trawling the news.' },
      { title: 'Focus for the real work', body: 'The busywork runs in the background, so your hours go to what only you can do.' },
    ],
    intent: 'focus-and-flow',
    departments: ['personal-productivity', 'marketing'],
    agents: ['luc', 'sally', 'dora'],
    seo: {
      title: 'Focus & flow workflow — your day, run for you | Implify',
      description:
        'A stitched focus-and-flow workflow: AI agents plan your day, triage your inbox and deliver a daily digest with competitor intelligence — so you stay in flow on the work that matters.',
      keywords: 'AI daily planning, inbox automation, daily digest, competitor intelligence, personal productivity workflow, focus automation, AI executive assistant',
    },
  },
];

// Lookups by slug (used by the page templates + the hub cross-links).
export const departmentBySlug = Object.fromEntries(departments.map((d) => [d.slug, d]));
export const workflowBySlug = Object.fromEntries(workflows.map((w) => [w.slug, w]));

export default { departments, workflows, departmentBySlug, workflowBySlug, resolveAgents };
